import type { DraftPayment } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import {
  type ApolloError,
  type DocumentNode,
  gql,
  useQuery,
} from "@apollo/client";
import { useEffect } from "react";

export const useGetDraftPayment = (
  draftPaymentId: string | null,
  options?: {
    skip?: boolean;
    refetchQueries?: string[] | DocumentNode[];
  },
): {
  data: DraftPayment | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
} => {
  const { tenantId } = useTenant();

  const { data, loading, error, refetch } = useQuery(GET_DRAFT_PAYMENT_QUERY, {
    variables: {
      draftPaymentId,
      tenantId,
    },
    skip: options?.skip || !draftPaymentId || !tenantId,
  });

  return {
    data: data?.getDraftPayment || null,
    loading,
    error,
    refetch,
  };
};

export const useGetAllDraftPayments = (
  invoiceIds?: string[] | null,
  options?: {
    skip?: boolean;
    refetchQueries?: string[] | DocumentNode[];
  },
): {
  data: DraftPayment[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
} => {
  const { tenantId } = useTenant();

  const { data, loading, error, refetch } = useQuery(
    GET_ALL_DRAFT_PAYMENTS_QUERY,
    {
      variables: {
        tenantId,
        invoiceIds: invoiceIds || undefined,
      },
      skip: options?.skip || !tenantId,
    },
  );

  useEffect(() => {
    console.log({ error, data });
  }, [error, data]);

  return {
    data: data?.draftPayments || [],
    loading,
    error,
    refetch,
  };
};

export const DRAFT_PAYMENT_FIELDS = gql`
  fragment DraftPaymentFields on DraftPaymentType {
    draftPaymentId
    tenantId
    status
    amount
    rawAmount
    discount
    createdAt
    recipientName
    recipientIban
    currency
    purpose
    fullPurpose
    executionDate
  }
`;

export const GET_DRAFT_PAYMENT_QUERY = gql`
  query DraftPayment($draftPaymentId: String!, $tenantId: String!) {
    draftPayment(draftPaymentId: $draftPaymentId, tenantId: $tenantId) {
      ...DraftPaymentFields
    }
  }
  ${DRAFT_PAYMENT_FIELDS}
`;

export const GET_ALL_DRAFT_PAYMENTS_QUERY = gql`
  query DraftPayments($tenantId: String!, $invoiceIds: [String!]) {
    draftPayments(tenantId: $tenantId, invoiceIds: $invoiceIds) {
      ...DraftPaymentFields
      invoices {
        invoiceId
        invoiceNo
        totalAmount
        currency
      }
    }
  }
  ${DRAFT_PAYMENT_FIELDS}
`;
